import { AUTH_SERVER_BASE_URL, PLATFORM_SERVER_BASE_URL } from '../config';

import { FABRIC_BASE_URL } from '../config';

declare global {
    interface Window {
        AuthUiClient: {
            Auth: typeof AuthUiClient.Auth;
            AuthError: typeof AuthUiClient.AuthError;
            AuthErrorFactory: typeof AuthUiClient.AuthErrorFactory;
            PlatformJwt: typeof AuthUiClient.PlatformJwt;
            [key: string]: unknown;
        };
        auth: unknown;
    }
}

const isAuthlibLoaded = (): boolean => {
    return typeof window !== 'undefined' && window.AuthUiClient?.Auth !== undefined;
};

export const initializeAuthlib = (): void => {
    if (typeof window === 'undefined') return;
    if (getAuthFromWindow() !== null) return;
    if (!isAuthlibLoaded()) return;

    const auth = window.AuthUiClient.Auth.getInstance({
        baseUrl: AUTH_SERVER_BASE_URL,
        defaultRedirectUrl: window.location.href,
        system: 'platform',
        autoRenewToken: true,
        logLevel: 'debug',
    });

    window.auth = auth;
};

export const checkAndRestoreSession = async (): Promise<boolean> => {
    const auth = getAuthFromWindow();

    if (!auth) {
        console.log('No auth found');
        return false;
    }

    try {
        const token = await auth.getToken();
        return Boolean(token);
    } catch (error) {
        console.error('Session restore error:', error);
        return false;
    }
};

export const getErrorMessage = (
    error: unknown,
    fallback = 'An unexpected error occurred',
): string => {
    if (error instanceof window.AuthUiClient.AuthError) {
        return error.getUserMessage();
    }

    if (error instanceof Error) {
        return error.message;
    }

    if (typeof error === 'string') {
        return error;
    }

    return fallback;
};

export const getAuthFromWindow = (): AuthUiClient.Auth | null => {
    if (typeof window === 'undefined' || !window.auth) {
        return null;
    }

    return window.auth as AuthUiClient.Auth;
};

export const getAccessToken = async (): Promise<string | undefined> => {
    try {
        const auth = getAuthFromWindow();
        return auth ? await auth.getToken() : undefined;
    } catch (err) {
        console.error(getErrorMessage(err));
        return undefined;
    }
};

export const apiFetch = async (url: string, init: RequestInit = {}): Promise<Response> => {
    const token = await getAccessToken();

    const resolvedUrl = url.startsWith('/') ? `${FABRIC_BASE_URL}${url}` : url;

    const response = await fetch(resolvedUrl, {
        ...init,
        headers: {
            ...init.headers,
            Authorization: `Bearer ${token}`,
        },
    });

    if (response.status === 403) {
        window.dispatchEvent(new CustomEvent('app:unauthorized'));
    }

    return response;
};

export const signOut = async (): Promise<void> => {
    try {
        const auth = getAuthFromWindow();
        auth && (await auth.logout());
    } catch (err) {
        console.error(getErrorMessage(err));
    }
};

type Feature = {
    key: string;
    value: string;
    extendedProperty: string;
};

export type User = {
    user: {
        account: {
            accountId: string;
            email: string;
            status: string;
        };
        Feature?: Feature[];
    };
    systemSettings: {
        'com.boomi.platform.pendo.api.key': string;
    };
};

export const getUser = async (): Promise<User | null> => {
    const auth = getAuthFromWindow();
    const accountId = await auth?.getAccountId();

    const baseUrl =
        process.env['NODE_ENV'] === 'production'
            ? PLATFORM_SERVER_BASE_URL
            : 'http://localhost:8010/proxy';

    const response = await apiFetch(
        `${baseUrl}/restrpc/account/${accountId}/handler/com.boomi.platform.gwt.login.LoadUserHandler`,
        {
            method: 'POST',
            body: JSON.stringify({
                context: 'ATOMSPHERE',
                clientUserAgent: 'Boomi Connect',
            }),
        },
    );

    if (!response.ok) {
        console.error(response.statusText);
        return null;
    }

    return response.json() as Promise<User>;
};
